// Gráficos do Dashboard Operacional (ADR-0018, ADR-0032; ui.md, seção
// Gráficos): barras verticais por período (empilháveis), linhas de taxa,
// ranking em barras horizontais e minigráfico. SVG/HTML próprios, sem
// biblioteca. As cores chegam prontas (tokens `--serie-*`); o texto usa
// sempre `--ink`/`--muted`, nunca a cor da série.

import { botao, el } from "../ui.ts";

const SVG_NS = "http://www.w3.org/2000/svg";

function svgEl(tag: string, attrs: Record<string, string | number> = {}, titulo?: string): SVGElement {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  if (titulo) {
    const t = document.createElementNS(SVG_NS, "title");
    t.textContent = titulo;
    node.append(t);
  }
  return node;
}

/** Máscara de milhar (pt-BR) para número que não é data nem valor monetário. */
export function formatarNumero(n: number): string {
  return n.toLocaleString("pt-BR");
}

/** Taxa como "4,2%" (`null` = sem base: "—"). */
export function formatarTaxa(v: number | null, casas = 1): string {
  return v == null
    ? "—"
    : `${(v * 100).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;
}

export interface LayoutGrafico {
  /** Largura de cada grupo de barras (um grupo por categoria do eixo X). */
  larguraGrupo: number;
  /** Largura total do desenho — igual à disponível quando estica. */
  larguraTotal: number;
  /** `true` quando o contêiner precisa de rolagem horizontal. */
  precisaRolagem: boolean;
}

/**
 * Regra de layout do gráfico: se `numGrupos` grupos cabem na
 * `larguraDisponivel` usando pelo menos `larguraMinimaGrupo` cada, os grupos
 * esticam pra preencher a tela; senão mantêm a largura mínima e o gráfico
 * fica maior que a tela, pedindo rolagem em vez de comprimir as barras.
 */
export function calcularLayoutGrafico(
  numGrupos: number,
  larguraDisponivel: number,
  larguraMinimaGrupo = 50,
): LayoutGrafico {
  if (numGrupos <= 0) {
    return { larguraGrupo: larguraMinimaGrupo, larguraTotal: larguraDisponivel, precisaRolagem: false };
  }
  const larguraNatural = numGrupos * larguraMinimaGrupo;
  if (larguraNatural <= larguraDisponivel) {
    return {
      larguraGrupo: larguraDisponivel / numGrupos,
      larguraTotal: larguraDisponivel,
      precisaRolagem: false,
    };
  }
  return { larguraGrupo: larguraMinimaGrupo, larguraTotal: larguraNatural, precisaRolagem: true };
}

/**
 * Marcas "redondas" do eixo Y, de 0 até cobrir `maximo`: passo 1, 2, 2,5 ou 5
 * vezes uma potência de 10, para ter umas 3 ou 4 linhas de grade.
 */
export function marcasDoEixo(maximo: number): number[] {
  if (!(maximo > 0)) return [0, 1];
  const bruto = maximo / 3;
  const potencia = 10 ** Math.floor(Math.log10(bruto));
  const passo = [1, 2, 2.5, 5, 10].map((m) => m * potencia).find((v) => v >= bruto)!;
  const marcas: number[] = [];
  for (let i = 0; i * passo < maximo + passo * 0.999; i++) marcas.push(Number((i * passo).toPrecision(12)));
  return marcas;
}

/**
 * Quais períodos têm o valor escrito nos gráficos de tendência: só o atual
 * (o último — padrão, menos poluído) ou todos ("Valores de todos os
 * períodos", nos ajustes de exibição do Dashboard).
 */
export type RotulosGrafico = "atual" | "todos";

/** O período `i` (de `n`) tem o valor escrito? */
export function escreverValor(i: number, n: number, rotulos: RotulosGrafico): boolean {
  return rotulos === "todos" || i === n - 1;
}

/** Um valor escrito no gráfico: o texto e a altura (y da linha de base, em px do SVG). */
export interface RotuloPonto {
  y: number;
  texto: string;
}

/**
 * Rótulos de um mesmo período, sem se sobrepor: do mais alto para baixo, com
 * pelo menos `vao` px entre um e outro; o mesmo texto aparece uma vez só
 * (três linhas em "0,0%" viram um "0,0%"). Nenhum passa de `limite` (o eixo
 * X): perto dele o grupo sobe em vez de descer.
 */
export function afastarRotulos(rotulos: readonly RotuloPonto[], vao: number, limite = Infinity): RotuloPonto[] {
  const unicos = [...new Map(rotulos.map((r) => [r.texto, r])).values()].sort((a, b) => a.y - b.y);
  const ys: number[] = [];
  unicos.forEach((r, i) => ys.push(i === 0 ? r.y : Math.max(r.y, ys[i - 1]! + vao)));
  for (let i = ys.length - 1; i >= 0; i--) ys[i] = Math.min(ys[i]!, i === ys.length - 1 ? limite : ys[i + 1]! - vao);
  return unicos.map((r, i) => ({ y: ys[i]!, texto: r.texto }));
}

/** Uma parte de uma barra (ou a barra inteira, quando é uma parte só). */
export interface ParteSerie {
  rotulo: string;
  cor: string;
  valores: readonly number[];
}
/** Uma série de barras: uma barra por período, empilhada pelas partes. */
export interface SerieBarras {
  rotulo: string;
  partes: readonly ParteSerie[];
}

const ALTURA_PADRAO = 240;
const MARGEM_ESQUERDA = 48;
const MARGEM_BAIXO = 26;
const MARGEM_CIMA = 20;
/** Período mais estreito antes de o gráfico pedir rolagem. */
const LARGURA_MINIMA_PERIODO = 44;

/** Eixo Y com grade e rótulos; devolve a função valor → y. */
function eixoY(
  svg: SVGElement,
  marcas: readonly number[],
  largura: number,
  altura: number,
  rotulo: (v: number) => string,
  margemCima = MARGEM_CIMA,
): (v: number) => number {
  const topo = marcas[marcas.length - 1]!;
  const y = (v: number): number => margemCima + (altura - margemCima - MARGEM_BAIXO) * (1 - v / topo);
  for (const m of marcas) {
    svg.append(svgEl("line", { x1: MARGEM_ESQUERDA, x2: largura, y1: y(m), y2: y(m), class: m === 0 ? "grafico-eixo" : "grafico-grade" }));
    const t = svgEl("text", { x: MARGEM_ESQUERDA - 6, y: y(m) + 4, "text-anchor": "end", class: "grafico-marca" });
    t.textContent = rotulo(m);
    svg.append(t);
  }
  return y;
}

/** Rótulos do eixo X, pulando alguns quando não cabem lado a lado. */
function eixoX(svg: SVGElement, periodos: readonly string[], xCentro: (i: number) => number, largura: number, altura: number): void {
  const maior = periodos.reduce((a, p) => Math.max(a, p.length), 1);
  const pular = Math.max(1, Math.ceil((maior * 7 + 8) / largura));
  periodos.forEach((p, i) => {
    if (i % pular !== 0 && i !== periodos.length - 1) return;
    const t = svgEl("text", { x: xCentro(i), y: altura - 7, "text-anchor": "middle", class: "grafico-marca" });
    t.textContent = p;
    svg.append(t);
  });
}

function contorno(svg: SVGElement, rolar: boolean): HTMLElement {
  const box = el("div", { class: rolar ? "grafico-barras grafico-com-rolagem" : "grafico-barras" });
  box.append(svg);
  return box;
}

/**
 * Barras verticais por período: uma barra por série em cada período, cada
 * barra empilhada pelas partes da série. Valor total escrito em cima quando
 * cabe. Estica ou rola conforme `calcularLayoutGrafico`.
 */
export function graficoBarras(opcoes: {
  periodos: readonly string[];
  series: readonly SerieBarras[];
  larguraDisponivel: number;
  altura?: number;
  rotulos?: RotulosGrafico;
}): HTMLElement {
  const altura = opcoes.altura ?? ALTURA_PADRAO;
  const escrever = (i: number): boolean => escreverValor(i, opcoes.periodos.length, opcoes.rotulos ?? "atual");
  const layout = calcularLayoutGrafico(opcoes.periodos.length, opcoes.larguraDisponivel - MARGEM_ESQUERDA, LARGURA_MINIMA_PERIODO);
  const largura = layout.larguraTotal + MARGEM_ESQUERDA;
  const totais = opcoes.series.map((s) => opcoes.periodos.map((_, i) => s.partes.reduce((a, p) => a + (p.valores[i] ?? 0), 0)));
  const maximo = totais.flat().reduce((a, b) => Math.max(a, b), 0);
  const svg = svgEl("svg", {
    width: largura, height: altura, viewBox: `0 0 ${largura} ${altura}`, role: "img",
    "aria-label": `Gráfico de barras: ${opcoes.series.map((s) => s.rotulo).join(", ")}`,
  });
  const n = Math.max(1, opcoes.series.length);
  const vao = 2;
  const larguraBarra = Math.min(40, (layout.larguraGrupo * 0.72 - vao * (n - 1)) / n);
  // número que não cabe deitado sobre a barra vai em pé: reserva o espaço em cima
  const cabeDeitado = (v: number): boolean => larguraBarra >= formatarNumero(v).length * 6.2;
  const caracteresEmPe = totais.flatMap((ts) => ts.map((v, i) => (v > 0 && escrever(i) && !cabeDeitado(v) ? formatarNumero(v).length : 0)));
  const margemCima = Math.max(MARGEM_CIMA, 10 + Math.max(0, ...caracteresEmPe) * 6.6);
  const y = eixoY(svg, marcasDoEixo(maximo), largura, altura, formatarNumero, margemCima);
  const xCentro = (i: number): number => MARGEM_ESQUERDA + layout.larguraGrupo * (i + 0.5);
  opcoes.periodos.forEach((periodo, i) => {
    const x0 = xCentro(i) - (larguraBarra * n + vao * (n - 1)) / 2;
    opcoes.series.forEach((serie, j) => {
      const x = x0 + j * (larguraBarra + vao);
      let acumulado = 0;
      for (const parte of serie.partes) {
        const v = parte.valores[i] ?? 0;
        if (v <= 0) continue;
        const yTopo = y(acumulado + v);
        // 2px de vão entre as partes empilhadas (ui.md)
        const h = Math.max(1, y(acumulado) - yTopo - (acumulado > 0 ? vao : 0));
        svg.append(svgEl("rect", { x, y: yTopo, width: larguraBarra, height: h, fill: parte.cor, rx: 2 },
          `${serie.partes.length > 1 ? `${serie.rotulo} · ` : ""}${parte.rotulo} em ${periodo}: ${formatarNumero(v)}`));
        acumulado += v;
      }
      const total = totais[j]![i]!;
      if (total > 0 && escrever(i)) {
        const t = cabeDeitado(total)
          ? svgEl("text", { x: x + larguraBarra / 2, y: y(total) - 4, "text-anchor": "middle", class: "grafico-valor" })
          : svgEl("text", {
              x: 0, y: 0, "text-anchor": "start", "dominant-baseline": "middle", class: "grafico-valor",
              transform: `translate(${x + larguraBarra / 2}, ${y(total) - 4}) rotate(-90)`,
            });
        t.textContent = formatarNumero(total);
        svg.append(t);
      }
    });
  });
  eixoX(svg, opcoes.periodos, xCentro, layout.larguraGrupo, altura);
  return contorno(svg, layout.precisaRolagem);
}

export interface SerieLinha {
  rotulo: string;
  cor: string;
  /** Taxa por período (0–1); `null` = sem base naquele período (a linha quebra). */
  valores: readonly (number | null)[];
}

/**
 * Linhas de taxa por período, no mesmo eixo de tempo do `graficoBarras`
 * (mesma largura de período). Valor escrito no último ponto de cada linha, à
 * direita; com `rotulos: "todos"`, também acima de cada ponto anterior.
 */
export function graficoLinhas(opcoes: {
  periodos: readonly string[];
  series: readonly SerieLinha[];
  larguraDisponivel: number;
  altura?: number;
  rotulos?: RotulosGrafico;
}): HTMLElement {
  const altura = opcoes.altura ?? ALTURA_PADRAO;
  const margemDireita = 52; // rótulo do último ponto
  const layout = calcularLayoutGrafico(
    opcoes.periodos.length,
    opcoes.larguraDisponivel - MARGEM_ESQUERDA - margemDireita,
    LARGURA_MINIMA_PERIODO,
  );
  const largura = layout.larguraTotal + MARGEM_ESQUERDA + margemDireita;
  const maximo = opcoes.series.flatMap((s) => s.valores).reduce<number>((a, v) => Math.max(a, v ?? 0), 0);
  const marcas = marcasDoEixo(maximo * 100).map((m) => m / 100);
  const svg = svgEl("svg", {
    width: largura, height: altura, viewBox: `0 0 ${largura} ${altura}`, role: "img",
    "aria-label": `Gráfico de linhas: ${opcoes.series.map((s) => s.rotulo).join(", ")}`,
  });
  const y = eixoY(svg, marcas, largura - margemDireita, altura, (m) => formatarTaxa(m, m > 0 && m < 0.1 && Math.round(m * 1000) % 10 !== 0 ? 1 : 0));
  const xCentro = (i: number): number => MARGEM_ESQUERDA + layout.larguraGrupo * (i + 0.5);
  const doPeriodo = new Map<number, RotuloPonto[]>();
  for (const serie of opcoes.series) {
    let d = "";
    serie.valores.forEach((v, i) => {
      if (v == null) return;
      d += `${d && serie.valores[i - 1] != null ? "L" : "M"}${xCentro(i)},${y(v)} `;
    });
    svg.append(svgEl("path", { d, fill: "none", stroke: serie.cor, "stroke-width": 2, "stroke-linejoin": "round" }));
    let ultimo = -1;
    serie.valores.forEach((v, i) => {
      if (v == null) return;
      ultimo = i;
      svg.append(svgEl("circle", { cx: xCentro(i), cy: y(v), r: 4, fill: serie.cor, stroke: "var(--surface)", "stroke-width": 2 },
        `${serie.rotulo} em ${opcoes.periodos[i]}: ${formatarTaxa(v)}`));
    });
    if (ultimo >= 0) {
      const t = svgEl("text", { x: xCentro(ultimo) + 8, y: y(serie.valores[ultimo]!) + 4, class: "grafico-valor" });
      t.textContent = formatarTaxa(serie.valores[ultimo]!);
      svg.append(t);
    }
    serie.valores.forEach((v, i) => {
      if (v == null || i === ultimo || !escreverValor(i, opcoes.periodos.length, opcoes.rotulos ?? "atual")) return;
      doPeriodo.set(i, [...(doPeriodo.get(i) ?? []), { y: y(v) - 9, texto: formatarTaxa(v) }]);
    });
  }
  for (const [i, rotulos] of doPeriodo) {
    // logo acima do eixo X: a linha de base do texto não passa do zero
    for (const r of afastarRotulos(rotulos, 12, y(0) - 3)) {
      const t = svgEl("text", { x: xCentro(i), y: r.y, "text-anchor": "middle", class: "grafico-valor grafico-valor-ponto" });
      t.textContent = r.texto;
      svg.append(t);
    }
  }
  eixoX(svg, opcoes.periodos, xCentro, layout.larguraGrupo, altura);
  return contorno(svg, layout.precisaRolagem);
}

/** Minigráfico de barras, sem eixo, para o número de destaque. */
export function miniGrafico(opcoes: {
  valores: readonly (number | null)[];
  cor: string;
  largura: number;
  altura?: number;
  rotuloDe: (i: number, v: number) => string;
}): SVGElement {
  const altura = opcoes.altura ?? 40;
  const largura = Math.max(40, opcoes.largura);
  const maximo = opcoes.valores.reduce<number>((a, v) => Math.max(a, v ?? 0), 0) || 1;
  const svg = svgEl("svg", { width: largura, height: altura, viewBox: `0 0 ${largura} ${altura}`, "aria-hidden": "true" });
  const passo = largura / Math.max(1, opcoes.valores.length);
  opcoes.valores.forEach((v, i) => {
    if (v == null || v <= 0) return;
    const h = Math.max(1, (v / maximo) * (altura - 2));
    svg.append(svgEl("rect", { x: i * passo + 1, y: altura - h, width: Math.max(1, passo - 2), height: h, rx: 1.5, fill: opcoes.cor },
      opcoes.rotuloDe(i, v)));
  });
  return svg;
}

/** Uma linha do ranking. */
export interface ItemRanking {
  chave: string;
  rotulo: string;
  /** O que ordena e dimensiona a barra (taxa ou quantidade). */
  valor: number;
  /** As partes da barra, na ordem fixa delas (a cor segue a parte). */
  partes: readonly { rotulo: string; cor: string; valor: number }[];
  /** "10,2% · 166 de 1.634". */
  texto: string;
  /** Abaixo do volume mínimo: vai para o fim, esmaecido (ADR-0032 §9). */
  volumeBaixo: boolean;
}

/** Ordem do ranking: maior valor primeiro; volume baixo sempre no fim. */
export function ordenarRanking<T extends Pick<ItemRanking, "valor" | "volumeBaixo" | "rotulo">>(itens: readonly T[]): T[] {
  return [...itens].sort(
    (a, b) =>
      Number(a.volumeBaixo) - Number(b.volumeBaixo) ||
      b.valor - a.valor ||
      a.rotulo.localeCompare(b.rotulo, "pt-BR"),
  );
}

/** Quantos itens o ranking mostra antes do "Ver Todos". */
export const RANKING_VISIVEIS = 10;

/**
 * Ranking em barras horizontais (HTML): rótulo, barra segmentada e texto. Com
 * `aoEscolher`, cada linha é um botão (clicar filtra a tela por ela).
 */
export function rankingBarras(opcoes: {
  itens: readonly ItemRanking[];
  verTodos: boolean;
  aoVerTodos: () => void;
  aoEscolher?: (chave: string) => void;
  escolhido?: (chave: string) => boolean;
  vazio?: string;
}): HTMLElement {
  const ordenados = ordenarRanking(opcoes.itens);
  const maximo = ordenados.reduce((a, i) => Math.max(a, i.valor), 0) || 1;
  const lista = el("div", { class: "ranking" });
  for (const item of opcoes.verTodos ? ordenados : ordenados.slice(0, RANKING_VISIVEIS)) {
    const soma = item.partes.reduce((a, p) => a + p.valor, 0) || 1;
    const barra = el("span", { class: "ranking-barra", style: `width:${(item.valor / maximo) * 100}%` });
    for (const p of item.partes) {
      if (p.valor <= 0) continue;
      barra.append(el("span", { style: `flex:${p.valor / soma} 1 0;background:${p.cor}`, title: `${p.rotulo}: ${formatarNumero(p.valor)}` }));
    }
    const classe = `ranking-linha${item.volumeBaixo ? " ranking-volume-baixo" : ""}${opcoes.escolhido?.(item.chave) ? " ranking-escolhido" : ""}`;
    const conteudo = [
      el("span", { class: "ranking-rotulo", title: item.rotulo }, item.rotulo),
      el("span", { class: "ranking-trilho" }, barra),
      el("span", { class: "ranking-texto" }, item.texto, item.volumeBaixo ? el("span", { class: "ranking-nota" }, " · volume baixo") : ""),
    ];
    if (opcoes.aoEscolher) {
      const escolhido = opcoes.escolhido?.(item.chave) ?? false;
      const b = el("button", {
        type: "button",
        class: classe,
        "aria-pressed": String(escolhido),
        title: escolhido ? `Tirar o filtro de ${item.rotulo}` : `Filtrar por ${item.rotulo}`,
      }, ...conteudo);
      const escolher = opcoes.aoEscolher;
      b.addEventListener("click", () => escolher(item.chave));
      lista.append(b);
    } else {
      lista.append(el("div", { class: classe }, ...conteudo));
    }
  }
  if (ordenados.length === 0) lista.append(el("p", { class: "muted" }, opcoes.vazio ?? "Nada no período."));
  if (ordenados.length > RANKING_VISIVEIS) {
    const b = botao("link", {},
      opcoes.verTodos ? `Mostrar só os ${RANKING_VISIVEIS} Primeiros` : `Ver Todos (${ordenados.length})`);
    b.addEventListener("click", opcoes.aoVerTodos);
    lista.append(b);
  }
  return lista;
}

/** Legenda: marcador colorido + rótulo, sem interação. */
export function legendaGrafico(itens: readonly { rotulo: string; cor: string }[]): HTMLElement {
  return el(
    "div",
    { class: "grafico-legenda" },
    ...itens.map((s) =>
      el("span", { class: "grafico-legenda-item" }, el("span", { class: "grafico-legenda-cor", style: `background:${s.cor}` }), s.rotulo),
    ),
  );
}
